import type { SpaceAccountCalendarWebhookStatus } from "@/store/calendar/space-account-calendar/types";

/**
 * Calendar sync status based on webhook and sync state.
 * Excludes UI-only states like CONNECTING/DISCONNECTING.
 */
export type CalendarSyncStatus = "SYNCING" | "TRYING_TO_CONNECT" | "SYNCED" | "FAILED";

export interface CalendarSyncStatusParams {
  webhookStatus: SpaceAccountCalendarWebhookStatus | null;
  syncStartedAt: string | null;
  syncEndedAt: string | null;
  lastSyncEndedWithError: boolean | null;
  isConnected?: boolean;
}

/**
 * Determines the sync status for a calendar based on its sync state and webhook status.
 *
 * Status Priority (checked in order):
 * 1. TRYING_TO_CONNECT - webhook is retrying
 * 2. FAILED - webhook setup failed (INACTIVE)
 * 3. SYNCING - sync in progress, no error
 * 4. TRYING_TO_CONNECT - reconciliation retrying (sync in progress with error)
 * 5. SYNCED - sync completed successfully
 * 6. FAILED - reconciliation failed (sync ended with error)
 *
 * Status Conditions:
 *
 * SYNCING:
 *   - sync_started_at: set
 *   - sync_ended_at: null
 *   - last_sync_ended_with_error: false
 *
 * TRYING_TO_CONNECT (retrying reconciliation):
 *   - sync_started_at: set
 *   - sync_ended_at: null (still retrying before giving up)
 *   - last_sync_ended_with_error: true
 *
 * TRYING_TO_CONNECT (retrying webhook):
 *   - webhook_status: TRYING_TO_CONNECT
 *
 * SYNCED:
 *   - sync_started_at: set (irrelevant)
 *   - sync_ended_at: set
 *   - last_sync_ended_with_error: false
 *   - webhook_status: ACTIVE
 *
 * FAILED (reconciliation):
 *   - sync_started_at: set (irrelevant)
 *   - sync_ended_at: set
 *   - last_sync_ended_with_error: true
 *   - webhook_status: ACTIVE
 *
 * FAILED (webhook setup):
 *   - webhook_status: INACTIVE
 *
 * FAILED (both reconciliation + webhook setup):
 *   - webhook_status: INACTIVE
 *   - sync_started_at: set (irrelevant)
 *   - sync_ended_at: set
 *   - last_sync_ended_with_error: true
 *
 * Retrying:
 * - If Connection.is_connected is false, retry using OAuth reconnect.
 * - If Calendar's status is failed, hit the /calendar/retry_connection endpoint with that specific calendar ID.
 */
export const getCalendarSyncStatus = ({
  webhookStatus,
  syncStartedAt,
  syncEndedAt,
  lastSyncEndedWithError,
  isConnected = true,
}: CalendarSyncStatusParams): CalendarSyncStatus | null => {
  // FAILED (connection not connected) - takes highest precedence
  if (isConnected === false) {
    return "FAILED";
  }

  // TRYING_TO_CONNECT (webhook retrying) - takes precedence
  if (webhookStatus === "TRYING_TO_CONNECT") {
    return "TRYING_TO_CONNECT";
  }

  // FAILED (webhook setup failed)
  if (webhookStatus === "INACTIVE") {
    return "FAILED";
  }

  // SYNCING: sync started, not ended, no error
  if (syncStartedAt && !syncEndedAt && !lastSyncEndedWithError) {
    return "SYNCING";
  }

  // TRYING_TO_CONNECT (reconciliation retrying): sync started, not ended, has error
  if (syncStartedAt && !syncEndedAt && lastSyncEndedWithError) {
    return "TRYING_TO_CONNECT";
  }

  // SYNCED: sync ended, no error, webhook active
  if (syncEndedAt && !lastSyncEndedWithError && webhookStatus === "ACTIVE") {
    return "SYNCED";
  }

  // FAILED (reconciliation): sync ended with error, webhook active
  if (syncEndedAt && lastSyncEndedWithError && webhookStatus === "ACTIVE") {
    return "FAILED";
  }

  // Return null if webhookStatus is null or unknown and no sync state matched
  return null;
};
